import { Link } from "@mui/material";

import { COMPANY_OTHER_LISTS_SORT_KEY } from "@signalist/shared-types";

import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { SortParams } from "@/types/types";

import { CompanyOtherListsCell } from "../components/CompanyOtherListsCell/CompanyOtherListsCell";
import { ListPerson, ListPersonOtherLists } from "../schema/list";

import { getOrderStatus } from "./column-utils";

/**
 * The two "on other lists" columns of the lead-list table: the lead's own prospect on other lists,
 * and its company on other lists (PREC-2750). Kept out of allColumns.tsx (file-length cap).
 */
export const renderOtherListsColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_other.name";

	return {
		title: ts("otherLists"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "otherLists",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				{row.otherLists?.length ? (
					<Tooltip
						content={
							<ul className="gap-2">
								{row.otherLists
									?.filter((list) => !!list.assignStatus && !!list.list?.id)
									?.map((list: ListPersonOtherLists) => (
										<li key={`other-list-${list.list.id}-${list.list.name}`}>
											<Link href={`/app/lists/detail/${list.list.id}`} target="_blank">
												{list.list.name} ({ts(`assignStatus.${list.assignStatus}`)})
											</Link>
										</li>
									))}
							</ul>
						}
					>
						<div>
							{row.otherLists?.length}{" "}
							{row.otherLists?.map((list: ListPersonOtherLists) => list.list.name)?.join(",")}
						</div>
					</Tooltip>
				) : (
					<div>No other lists</div>
				)}
			</div>
		),
	};
};

/**
 * "Company was on other lists" (PREC-2750). Sorting sends the `companyOtherLists` sentinel, which
 * the API resolves to the number of other lists — so the column sorts by count, not by name.
 */
export const renderCompanyOtherListsColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => ({
	title: ts("companyOtherLists"),
	width: "fit-content",
	orderTitle: COMPANY_OTHER_LISTS_SORT_KEY,
	orderStatus: getOrderStatus(sortingInbox, COMPANY_OTHER_LISTS_SORT_KEY),
	uid: "companyOtherLists",
	hideable: true,
	moveable: true,
	show: true,
	render: (row: ListPerson) => <CompanyOtherListsCell lists={row.companyOtherLists} />,
});
